import { ArrowUpRight } from "lucide-react";
import type { Observation } from "@/types/agent.type";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import ChatMarkdown from "@/app/routes/dashboard/components/ChatMarkdown";
import {
  OBSERVATION_TYPES,
  citationOf,
  observationEvidenceDetail,
  openSourceLabel,
} from "./evidenceDisplay";
import { formatFullDate } from "./signalMeta";
import SourceFavicon from "./SourceFavicon";

/** The url as a reader checks it: host and path, no scheme, no "www.". */
function displayUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = `${u.pathname}${u.search}`.replace(/\/$/, "");
    return `${u.hostname.replace(/^www\./, "")}${path}`;
  } catch {
    return url;
  }
}

/**
 * One source, read in full: where it came from and when, its headline when it
 * has a real one, what it says, and the way out to it.
 *
 * Field-reporter citations carry their own gist. The older observation types
 * keep the detail text they always had (a video analysis is markdown), so a
 * signal grounded before the reporter still opens onto something readable.
 */
export default function SignalSourceDetail({ observation }: { observation: Observation }) {
  const c = citationOf(observation);
  const isCitation = observation.type === OBSERVATION_TYPES.REPORT_CITATION;
  const isAnalysis =
    observation.type === OBSERVATION_TYPES.REFERENCE_VIDEO_ANALYSIS ||
    observation.type === OBSERVATION_TYPES.REFERENCE_IMAGE_ANALYSIS;
  const text = isCitation ? c.gist : observationEvidenceDetail(observation);

  return (
    <article className="sig-srcd">
      <div className="sig-srcd__src">
        <SourceFavicon host={c.host} size={15} />
        <span className="sig-srcd__host">{c.host ?? "Source"}</span>
        <span className="sig-srcd__date">· Observed {formatFullDate(c.observedAt)}</span>
      </div>

      {c.title ? (
        <h3 className="sig-srcd__title" dir="auto">
          {scrubVendorTerms(c.title)}
        </h3>
      ) : null}

      {text ? (
        isAnalysis ? (
          <div className="sig-srcd__gist sig-md" dir="auto">
            <ChatMarkdown content={text} />
          </div>
        ) : (
          <p className="sig-srcd__gist" dir="auto">
            {scrubVendorTerms(text)}
          </p>
        )
      ) : null}

      {c.url ? (
        <>
          <p className="sig-srcd__url" title={c.url}>
            {displayUrl(c.url)}
          </p>
          <a
            className="sig-srcd__open"
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {openSourceLabel(c.host)}
            <ArrowUpRight aria-hidden="true" />
          </a>
        </>
      ) : null}
    </article>
  );
}
